/**
 * The approval queue, as rows.
 *
 * This is the screen where a person's click becomes money leaving an account,
 * so the job of this module is to make sure the button never says something the
 * system will not do. Three things are decided here rather than in the
 * component, because each of them is a correctness question and not a layout
 * question:
 *
 *  1. **What approving will actually do.** With `WRITE_ACTIONS_DRY_RUN` on —
 *     which it is today — approving sends nothing upstream. The button must say
 *     so before the click, not the toast afterwards.
 *  2. **Which rows cannot be approved at all.** `cancel_order` has no adapter
 *     implementation; the API answers it with an instruction to do it by hand.
 *     Presenting it as approvable would produce a failure that reads like an
 *     outage.
 *  3. **What is actually at stake.** A refund shows its amount. A subscription
 *     change shows no amount, and showing $0.00 for it would be a lie about a
 *     change that still matters.
 */

export type ActionStatus =
  | 'proposed'
  | 'awaiting_approval'
  | 'approved'
  | 'executing'
  | 'confirmed'
  | 'failed'
  | 'rejected'
  | 'blocked';

export type ActionType =
  | 'refund'
  | 'retention_refund'
  | 'cancel_subscription'
  | 'change_frequency'
  | 'change_quantity'
  | 'skip_months'
  | 'reactivate_subscription'
  | 'cancel_order'
  | 'marketing_unsubscribe'
  | 'assign_rma';

/** The policy slice the API distils out of the run's facts. */
export interface PolicyContext {
  offerPercent: number | null;
  offerAmountCents: number | null;
  offerReason: string | null;
  noOfferReason: string | null;
  orderTotalCents: number | null;
  guaranteeStatus: string | null;
  guaranteeWindowDays: number | null;
  daysSinceLatestOrder: number | null;
  latestOrderPaymentStatus: string | null;
  /** Paid, and no parcel shipped when the run looked. Absent on older API builds. */
  parcelNotShipped?: boolean;
}

/**
 * The order tags the ticket's conversation wrote or will write into UltraCart
 * (API: approvals/order-tags.ts). Per TICKET — every action on one ticket
 * carries the same value.
 */
export interface OrderTags {
  state: 'written' | 'unconfirmed' | 'dry_run' | 'failed' | 'pending' | 'skipped' | 'person' | 'none';
  orderId: string | null;
  properties: Record<string, string>;
  dueAt: string | null;
  at: string | null;
  detail: string | null;
}

export interface ApiAction {
  id: string;
  type: ActionType;
  status: ActionStatus;
  payload: Record<string, unknown> | null;
  amountCents: number | null;
  actor: string;
  idempotencyKey: string;
  reversible: boolean;
  blockedReasons: string[];
  readBack: unknown;
  auditTs: string;
  executedAt: string | null;
  approvedAt: string | null;
  approvedBy: { email: string; name: string | null } | null;
  handledAt?: string | null;
  handledBy?: { email: string; name: string | null } | null;
  orderTags?: OrderTags | null;
  run: {
    id: string;
    mode: 'shadow' | 'assisted' | 'auto';
    draftReply: string | null;
    escalationReason?: string | null;
    policyContext?: PolicyContext;
    ticket: {
      id: string;
      externalId: string;
      intent: string | null;
      customerName: string | null;
      customerEmail: string | null;
      brand: { id: string; name: string; slug: string };
    };
  };
}

export interface ApprovalsResponse {
  actions: ApiAction[];
  dryRun: boolean;
  /** Rows matching the tab and filters, over every page. */
  total: number;
  page: number;
  pageSize: number;
  /** Per tab (action status) under the same filters; Failed / Blocked count what is still to handle. */
  counts: Record<string, number>;
  /** The Refund Reasons a person may pick before executing a refund. */
  refundReasons?: string[];
  /** What a held refund or replacement can be rejected for; Zoye answers some itself. */
  rejectionReasons?: RejectionReasonOption[];
}

export interface RejectionReasonOption {
  value: string;
  label: string;
  /** Which rows it fits: refunds, replacements, or any. */
  for: 'refund' | 'replacement' | 'any';
  /** Zoye sends the customer the standard answer; otherwise the ticket goes to Tier 2. */
  zoyeReplies: boolean;
}

/**
 * The rejection reasons that fit this row, or none for a row whose rejection
 * tells nobody (a subscription change, say): those take a note only.
 */
export function rejectionReasonsFor(
  type: ActionType,
  reasons: readonly RejectionReasonOption[],
): RejectionReasonOption[] {
  // Replacement rows arrive though the type list does not name them (it
  // backs the labels, and the filter lists replacements on its own).
  const kind = MONEY_TYPES.has(type) ? 'refund' : (type as string) === 'replacement_shipment' ? 'replacement' : null;
  if (!kind) return [];
  return reasons.filter((reason) => reason.for === kind || reason.for === 'any');
}

/** Types that move money. Only these carry an amount worth rendering. */
export const MONEY_TYPES: ReadonlySet<ActionType> = new Set(['refund', 'retention_refund']);

/**
 * Types with no adapter implementation.
 *
 * Empty since `cancelOrder` landed. Kept as a set rather than deleted because
 * the queue must always have somewhere to put "the adapter cannot do this yet",
 * and discovering that at the moment it is needed is how a screen ends up
 * showing a generic failure for a known gap.
 *
 * Note that an EMPTY set is not the same as "every cancellation will work":
 * the adapter refuses an order that has already shipped, and that refusal comes
 * back as an error message the operator reads, not as an unimplemented type.
 */
export const UNIMPLEMENTED_TYPES: ReadonlySet<ActionType> = new Set([]);

const TYPE_LABELS: Record<ActionType, string> = {
  refund: 'Refund',
  retention_refund: 'Retention refund',
  cancel_subscription: 'Cancel subscription',
  change_frequency: 'Change frequency',
  change_quantity: 'Change quantity',
  skip_months: 'Skip months',
  reactivate_subscription: 'Reactivate subscription',
  cancel_order: 'Cancel order',
  marketing_unsubscribe: 'Marketing unsubscribe',
  assign_rma: 'Assign RMA',
};

/** The Approvals "action type" filter: every type a row can carry. */
export const TYPE_FILTER_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  ...(Object.entries(TYPE_LABELS) as Array<[string, string]>).map(([value, label]) => ({ value, label })),
  { value: 'update_shipping_address', label: 'Change shipping address' },
  { value: 'replacement_shipment', label: 'Replacement shipment' },
  { value: 'add_refund_block', label: 'Refund block' },
  { value: 'assign_rma', label: 'Assign RMA' },
];

export function typeLabel(type: ActionType): string {
  return TYPE_LABELS[type] ?? String(type).replace(/_/g, ' ');
}

export interface ApprovalRow {
  id: string;
  type: ActionType;
  typeLabel: string;
  status: ActionStatus;
  /** Cents, or null for an action that moves no money. Never 0 as a stand-in. */
  amountCents: number | null;
  /** The one-line "what this does", built from the payload. */
  detail: string;
  customer: string | null;
  ticketRef: string;
  ticketId: string;
  brand: string;
  mode: 'shadow' | 'assisted' | 'auto';
  draftReply: string | null;
  blockedReasons: string[];
  approvable: boolean;
  /** Set when it is not approvable: what the operator should do instead. */
  instead: string | null;
  /** A step to take BEFORE approving, shown on the row; null when none. */
  firstStep: string | null;
  approvedBy: string | null;
  /** A person marked this failed/blocked row handled: who, and when. */
  handled: { by: string | null; at: string } | null;
  at: string;
  /** The policy decision, as sentences. Empty when the run recorded none. */
  policy: string[];
  /** UltraCart's Refund Reason Zoye chose, on a refund row; null otherwise. */
  refundReason: string | null;
  /** The ticket's categorised intent slug. */
  intent: string | null;
  /** The order this action is on, from its payload; null for a subscription-only action. */
  orderId: string | null;
  orderTags: OrderTags | null;
  /**
   * The action changed what the customer has or paid, so the conversation
   * should have planned tags (Save Initiated at least). "Nothing planned" on
   * such a row is the miss the Done list exists to catch.
   */
  tagsExpected: boolean;
}

/** Actions after which the order should carry save tags. */
export const TAGGED_TYPES: ReadonlySet<ActionType> = new Set([
  'refund',
  'retention_refund',
  'cancel_subscription',
  'cancel_order',
]);

export function toApprovalRows(actions: readonly ApiAction[]): ApprovalRow[] {
  return actions.map((action) => {
    const { ticket } = action.run;
    const blocked = UNIMPLEMENTED_TYPES.has(action.type);

    return {
      id: action.id,
      type: action.type,
      typeLabel: typeLabel(action.type),
      status: action.status,
      // Money is read from the column, not from the payload: the gate wrote
      // the column, and a payload percent multiplied on the client is exactly
      // the sort of second opinion that ends in the wrong refund.
      amountCents: MONEY_TYPES.has(action.type) ? action.amountCents : null,
      detail: describePayload(action.type, action.payload ?? {}),
      customer: ticket.customerName ?? ticket.customerEmail ?? null,
      ticketRef: `#${ticket.externalId}`,
      ticketId: ticket.id,
      brand: ticket.brand.name,
      mode: action.run.mode,
      draftReply: action.run.draftReply,
      // The nightly re-check's tab marker is where the row sits, not why the gate refused it.
      blockedReasons: action.blockedReasons.filter((reason) => reason !== 'recheck:awaiting_customer'),
      approvable:
        !blocked &&
        (isApprovableStatus(action.status) ||
          isRetryableFailure(action.status, action.blockedReasons)),
      instead: blocked
        ? 'The UltraCart adapter cannot cancel an order yet. Cancel it in UltraCart by hand, then reject this with a note saying you did.'
        : null,
      firstStep: firstStepFor(action),
      // No person on a row the agent executed itself (auto mode, 2026-10-08):
      // say who did it rather than leave the column blank.
      approvedBy:
        action.approvedBy?.name ??
        action.approvedBy?.email ??
        (action.actor === 'system' && action.executedAt ? 'Zoye (automatic)' : null),
      handled: action.handledAt
        ? { by: action.handledBy?.name ?? action.handledBy?.email ?? null, at: action.handledAt }
        : null,
      at: action.auditTs,
      policy: policyLines(
        action.run.policyContext ?? null,
        action.run.escalationReason ?? null,
        str(action.payload?.kind),
      ),
      refundReason: MONEY_TYPES.has(action.type) ? str(action.payload?.refundReason) : null,
      intent: ticket.intent,
      orderId: str(action.payload?.orderId),
      orderTags: action.orderTags ?? null,
      tagsExpected: TAGGED_TYPES.has(action.type),
    };
  });
}

/**
 * The stop-the-parcel hold, said on the row (#1923313, Deivydas 2026-10-13):
 * a refund on a paid order with nothing shipped waits for a person to stop
 * the parcel, and the queue only said "awaiting approval".
 */
export function firstStepFor(action: Pick<ApiAction, 'type' | 'status' | 'run'>): string | null {
  if (!MONEY_TYPES.has(action.type) || !isApprovableStatus(action.status)) return null;
  if (action.run.policyContext?.parcelNotShipped !== true) return null;
  return 'Paid, not shipped: cancel the order in ShipStation first (possible only while no label is printed), then approve. If the label is printed, reject this and handle it as a return.';
}

/**
 * The policy decision, spelled out.
 *
 * Every line names its numbers — the rung, the base, the day count — because
 * an amount with no derivation gets approved on trust, and this queue exists
 * so it never has to be. A missing piece is omitted, never zero-filled.
 */
export function policyLines(
  context: PolicyContext | null,
  escalationReason: string | null,
  /** The action payload's `kind` — an item refund is not priced by the ladder. */
  paymentKind: string | null = null,
): string[] {
  const lines: string[] = [];
  if (context) {
    if (paymentKind === 'item_refund') {
      // #1952681 (ops QA 2026-10-26): "Policy authorised 10%" over a $33.96
      // refund read as a mistake. The line's price is the amount; the
      // ladder's figure is a different offer.
      const ladder = context.offerPercent !== null ? ` The save ladder stood at ${context.offerPercent}%.` : '';
      lines.push(`Item refund: the price paid for that one line, not a save-ladder figure.${ladder}`);
    } else if (context.offerPercent !== null) {
      const base =
        context.orderTotalCents !== null
          ? ` of $${(context.orderTotalCents / 100).toFixed(2)}`
          : '';
      const path = context.offerReason ? ` — via the ${context.offerReason.replace(/_/g, ' ')}` : '';
      lines.push(`Policy authorised ${context.offerPercent}%${base}${path}.`);
    } else if (context.noOfferReason) {
      lines.push(`Policy authorised no offer — ${context.noOfferReason.replace(/_/g, ' ')}.`);
    }

    if (context.guaranteeStatus) {
      const window =
        context.guaranteeWindowDays !== null ? ` the ${context.guaranteeWindowDays}-day` : ' the';
      const day =
        context.daysSinceLatestOrder !== null ? ` (day ${context.daysSinceLatestOrder})` : '';
      lines.push(
        context.guaranteeStatus === 'inside'
          ? `Inside${window} guarantee${day}.`
          : `Outside${window} guarantee${day}.`,
      );
    }

    if (context.latestOrderPaymentStatus && context.latestOrderPaymentStatus !== 'Processed') {
      lines.push(`Order payment status: ${context.latestOrderPaymentStatus}.`);
    }
  }
  if (escalationReason) {
    lines.push(`The run escalated: ${escalationReason}`);
  }
  return lines;
}

/**
 * A failed action may be RETRIED only when the failure was clean: the write
 * errored and nothing changed, which the API records as `failed` WITH
 * blockedReasons (the error text). The other failed flavour — ok-but-
 * unproven, "may have landed but read-back could not prove it" — records no
 * reasons and stays locked: retrying a write that may have landed is the
 * double this queue exists to prevent (pre-pilot QA, console #18).
 */
export function isRetryableFailure(
  status: ActionStatus,
  blockedReasons: readonly string[],
): boolean {
  return status === 'failed' && blockedReasons.length > 0;
}

/** Mirrors `assertApprovable` on the API. Anything else is already decided. */
export function isApprovableStatus(status: ActionStatus): boolean {
  // `approved` is the pre-vetted lane (auto mode, dry-run validations) and
  // still needs the human press — money never moves on its own.
  return status === 'awaiting_approval' || status === 'proposed' || status === 'approved';
}

/**
 * What the button promises.
 *
 * With the dry run on, approving records the decision and sends nothing. Saying
 * "Approve" alone would be a lie by omission on the only screen where that
 * matters, so the label itself carries the state.
 */
export function approveLabel(dryRun: boolean): string {
  return dryRun ? 'Approve (dry run)' : 'Approve and send';
}

export function approveConsequence(row: ApprovalRow, dryRun: boolean): string {
  if (dryRun) {
    return 'Records your approval. WRITE_ACTIONS_DRY_RUN is on, so nothing reaches UltraCart and no money moves. The action stays in the queue as approved, not confirmed.';
  }
  if (row.amountCents !== null) {
    return 'Sends the refund to UltraCart now. This is not reversible from here.';
  }
  return 'Sends the change to UltraCart now.';
}

/**
 * A one-line description of the action, from its payload.
 *
 * Deliberately conservative: a field that is missing is omitted rather than
 * rendered as "undefined", and nothing here recomputes an amount.
 */
export function describePayload(type: ActionType, payload: Record<string, unknown>): string {
  const parts: string[] = [];
  const order = str(payload.orderId);
  const subscription = str(payload.subscriptionId);

  if (order) parts.push(`order ${order}`);
  if (subscription) parts.push(`subscription ${subscription}`);

  const percent = num(payload.percent);
  if (percent !== null && MONEY_TYPES.has(type)) parts.push(`${percent}%`);

  const quantity = num(payload.quantity);
  if (quantity !== null) parts.push(`quantity ${quantity}`);

  const frequency = str(payload.frequency);
  if (frequency) parts.push(`every ${frequency}`);

  const months = num(payload.monthsToSkip);
  if (months !== null) parts.push(`skip ${months} month${months === 1 ? '' : 's'}`);

  const reason = str(payload.reason);
  if (reason) parts.push(`— ${reason}`);

  if (type === 'marketing_unsubscribe') {
    const emails = Array.isArray(payload.emails) ? payload.emails.filter((email) => typeof email === 'string') : [];
    // A STOP by text (2026-10-21) carries the channel; email unsubscribes do not.
    parts.push(payload.channel === 'sms' ? 'marketing texts (SMS)' : 'marketing email');
    if (emails.length > 0) parts.push(emails.join(', '));
  }

  return parts.length > 0 ? parts.join(' · ') : 'No detail recorded on this action.';
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * The money sitting in the queue.
 *
 * Only rows that are still waiting count. Including already-approved ones would
 * make the figure grow all day and never fall, which is the opposite of what a
 * queue total is for.
 */
export function outstandingCents(rows: readonly ApprovalRow[]): number {
  return rows
    .filter((row) => isApprovableStatus(row.status))
    .reduce((total, row) => total + (row.amountCents ?? 0), 0);
}
